import { useMemo } from 'react'
import useSharedCollection from '../firebase/useSharedCollection'
import { ADRESS_STATUS_KEYS } from '../helpers/adressHelpers/adressStatusLabels'
import { filterByLocation } from '../helpers/locations/locationFilter'

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// Returns the Adresses for each housevisit status, bucketed.
//
// Previously this hook opened FIVE separate `useFirestoreGeneral` listeners
// (one per status), which meant every consumer (dashboard card, live-report
// widget) doubled the listener count. We now read the whole `Adresses`
// collection once via `useSharedCollection` — which multicasts across all
// consumers — and bucket in memory. One backend listener total, regardless
// of how many cards/widgets are rendered.
//
// The bucket keys come from ADRESS_STATUS_KEYS, never from a local list. This
// used to whitelist five keys by hand and so dropped every household on
// 'Onbereikbaar' — the charts simply left them out, without an error to notice
// (inbox #121).
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook

/**
 * @param {string} [selectedLocationId] - Optioneel: alleen deze locatie; leeg = alle
 * @returns {Record<string, Array<object>>} Households per status key, in funnel order
 */
/**
 * Zoals useAddressStatusCounts, plus of de huishoudens al geladen zijn. Zolang
 * ze onderweg zijn is er nog niets te tellen, en dat is iets anders dan nul:
 * de kaart zei tijdens het laden 'Nog geen huisbezoeken' (testronde 2026-10-13).
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {string} [selectedLocationId] - Alleen huishoudens op deze locatie; leeg = alle
 * @returns {{ loaded: boolean, counts: Record<string, Array<object>> }}
 */
const useAddressStatusCountsState = (selectedLocationId = '') => {
    const addresses = useSharedCollection('Adresses')
    const loaded = Array.isArray(addresses)

    const counts = useMemo(() => {
        const buckets = ADRESS_STATUS_KEYS.reduce((acc, key) => {
            acc[key] = []
            return acc
        }, /** @type {Record<string, Array>} */ ({}))

        const list = filterByLocation(addresses, selectedLocationId)
        list.forEach((doc) => {
            const status = doc?.Status
            if (status && buckets[status]) buckets[status].push(doc)
        })

        return buckets
    }, [addresses, selectedLocationId])

    return { loaded, counts }
}

const useAddressStatusCounts = (selectedLocationId = '') => useAddressStatusCountsState(selectedLocationId).counts

export { useAddressStatusCountsState }
export default useAddressStatusCounts
